import React, { ReactElement, useState } from 'react';
import { CalendarCustomIcon, CloseIcon, DividerLongIcon } from '../../assets/icons';
import {
  Customer,
  CustomerSubscription,
  Invoice,
  INVOICE_TYPE_ISSUED,
  IssuedInvoice,
} from '../types';
import { ActionMenuItem, GrowegyMenu } from '../../commons/components/GrowegyMenu';
import clsx from 'clsx';
import { formatDateTimeCell } from '../../util/date-utils';
import { formatMoney } from '../../util/utils';
import { ButtonColored } from '../../program/components/ButtonColored';
import { BILLING_PLAN_YEAR, getBillingPlan } from '../billingPlans';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import {
  getInvoiceMaxEnd,
  isInvoiceNonPastDue,
  isInvoicePastDue,
  isInvoicePayable,
  nextCreditReportDate,
} from '../utils';
import { CanceledBanner } from './cancellation/CanceledBanner';
import { TaxIdRequiredBanner } from './cancellation/TaxIdRequiredBanner';
import { PayAllButton } from './PayAllButton';
import { Info } from '../../commons/components/Info';
import { Button } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmModal } from '../../commons/components/ConfirmModal';
import { Reactivation } from './Reactivation';
import { InvoiceTable } from './InvoiceTable';
import { ResumeSubscriptionButton } from './ResumeSubscriptionButton';

type BillingHeaderColumnProps = {
  top: string;
  bottom: string | ReactElement;
  isBottomRed?: boolean;
};
type BillingHeaderSectionProps = {
  column1?: BillingHeaderColumnProps;
  column2?: BillingHeaderColumnProps;
  action?: ReactElement;
};

const BillingHeaderColumn = ({ top, bottom, isBottomRed }: BillingHeaderColumnProps) => {
  return (
    <>
      <div data-test="billing__header-column-top" className="billing__header-column-top">
        {top}
      </div>
      <div
        data-test="billing__header-column-value"
        className={clsx('billing__header-column-bottom', {
          'billing__header-column-bottom--red': isBottomRed,
        })}
      >
        {bottom}
      </div>
    </>
  );
};

const HeaderSectionColumns = ({ column1, column2, action }: BillingHeaderSectionProps) => (
  <div className="billing__header-columns">
    {column1 && (
      <div>
        <BillingHeaderColumn {...column1} />
      </div>
    )}
    {column2 && (
      <div
        className={clsx({
          'billing__header-middle-column-margin': column2.isBottomRed,
        })}
      >
        <BillingHeaderColumn {...column2} />
      </div>
    )}
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

export const BillingHeaderSections = ({ sections }: { sections: BillingHeaderSectionProps[] }) => {
  return (
    <div className="billing__header">
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          <div className="billing__header-section" data-test="billing__header-section">
            <HeaderSectionColumns {...section} />
          </div>
          {index < sections.length - 1 && (
            <>
              <DividerLongIcon className="billing__header-vertical-divider" />
              <div className="billing__header-horizontal-divider" />
            </>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const isAnnualPlanFullyPaid = (currentPlan: string, invoices: IssuedInvoice[]) => {
  const plan = getBillingPlan(currentPlan);
  if (plan?.name !== BILLING_PLAN_YEAR.name) {
    return false;
  }

  if (invoices.filter((i) => i.status !== 'PAID').length > 0) {
    return false;
  }

  const annualInvoices = invoices.filter((i) => i.billingPlanName === plan.name);
  return annualInvoices.length > 0 && annualInvoices.length % 3 === 0;
};

const getCurrentPlanSectionProps = ({
  currentPlan,
  onCancelClick,
  onSeeAnnualClick,
  onReactivateClick,
  changePlanOptions,
  customerSubscription,
  invoices,
}: {
  onCancelClick: () => void;
  onSeeAnnualClick: () => void;
  onReactivateClick: () => void;
  currentPlan: string;
  changePlanOptions: string[];
  customerSubscription: CustomerSubscription | null;
  invoices: IssuedInvoice[];
}) => {
  const menuItems: ActionMenuItem[] = [];
  if (changePlanOptions.includes(BILLING_PLAN_YEAR.name)) {
    menuItems.push({
      type: 'standard',
      text: 'See Annual Plan',
      icon: <CalendarCustomIcon />,
      dataTest: 'billing__see-annual-plan-menu-item',
      onClick: onSeeAnnualClick,
    });
  }
  if (customerSubscription && !customerSubscription.cancelAt) {
    menuItems.push({
      type: 'standard',
      text: isAnnualPlanFullyPaid(currentPlan, invoices) ? 'Cancel renewal' : 'Cancel plan',
      icon: <CloseIcon />,
      dataTest: 'billing__cancel-plan-menu-item',
      onClick: onCancelClick,
    });
  }

  const billingPlan = getBillingPlan(currentPlan);
  return {
    column1: { top: 'Current plan', bottom: billingPlan?.displayName ?? 'unknown' },
    column2: { top: 'Price', bottom: billingPlan?.scheduleDescription ?? 'unknown' },
    action: (
      <>
        {customerSubscription && menuItems.length > 0 ? (
          <div className="billing__header-cancelled-reactivate-container">
            <GrowegyMenu
              menuItems={menuItems}
              menuButtonDataTest="billing__manage-button"
              menuDataTest="billing__manage-menu"
              customToggle={() => (
                <ButtonColored
                  text={!!customerSubscription?.cancelAt ? 'Pending Cancel' : 'Manage'}
                />
              )}
            />
            {!!customerSubscription?.cancelAt && <ResumeSubscriptionButton />}
          </div>
        ) : (
          <div className="billing__header-cancelled-reactivate-container">
            <span className="billing__header-column-bottom">Canceled</span>
            <Button
              variant="primary"
              data-test="billing__plan-cancelled"
              className="button-apply"
              onClick={onReactivateClick}
            >
              Reactivate Plan
            </Button>
          </div>
        )}
      </>
    ),
  };
};

const getInvoicesSectionColumn = (
  invoices: IssuedInvoice[],
  header: string,
  isBottomRed: boolean
): BillingHeaderColumnProps | null => {
  if (invoices.length <= 0) return null;

  const totalAmountDue = invoices.reduce((acc, curr) => acc + curr.amountDue, 0);
  if (totalAmountDue <= 0) return null;

  const earliestDueDate = invoices.reduce((acc, curr) =>
    acc.dueDate.valueOf() < curr.dueDate.valueOf() ? acc : curr
  ).dueDate;
  const amountDue = formatMoney(`${totalAmountDue}`);
  const dueDate = formatDateTimeCell(earliestDueDate, 'MMM DD, YYYY');
  return {
    top: header,
    bottom: `${amountDue} on ${dueDate}`,
    isBottomRed: isBottomRed,
  };
};

const getInvoicesSection = (invoices: IssuedInvoice[]): BillingHeaderSectionProps | null => {
  const now = new Date();
  const openInvoices = invoices.filter((invoice) => invoice.status === 'OPEN');
  const pastDueInvoices = openInvoices.filter((inv) => isInvoicePastDue(inv, now));
  const uncollectibleInvoices = invoices.filter((invoice) => invoice.status === 'UNCOLLECTIBLE');
  const pastDueAndUncollectible = [...pastDueInvoices, ...uncollectibleInvoices];
  const nonPastDueInvoices = openInvoices.filter((value) => isInvoiceNonPastDue(value, now));

  const columns = [
    getInvoicesSectionColumn(pastDueAndUncollectible, 'Payment past due', true),
    getInvoicesSectionColumn(nonPastDueInvoices, 'Payment due', false),
  ].filter((x) => x != null) as BillingHeaderColumnProps[];
  if (columns.length === 0) return null;

  return {
    column1: columns[0],
    column2: columns.length > 1 ? columns[1] : undefined,
    action: <PayAllButton source="header" dataTest="billing-header__pay-all" invoices={invoices} />,
  };
};

const getNextPaymentDate = (
  invoices: IssuedInvoice[],
  cancelAt: Date | null | undefined
): Date | null => {
  if (cancelAt) {
    return cancelAt;
  }
  if (invoices.length > 0) {
    return invoices
      .map((i) => getInvoiceMaxEnd(i))
      .reduce((acc, curr) => (acc.valueOf() > curr.valueOf() ? acc : curr));
  }
  return null;
};

const getNextPaymentSection = (
  currentPlan: string,
  invoices: IssuedInvoice[],
  customerSubscription: CustomerSubscription | null
): BillingHeaderSectionProps | null => {
  if (!customerSubscription) return null;
  if (invoices.length === 0 || invoices.some((x) => isInvoicePayable(x))) return null;

  const lastEndDate = getNextPaymentDate(invoices, customerSubscription?.cancelAt);
  return {
    column1: {
      top: !!customerSubscription?.cancelAt
        ? 'Active until'
        : isAnnualPlanFullyPaid(currentPlan, invoices)
        ? 'To be renewed'
        : 'Next invoice',
      bottom: formatDateTimeCell(lastEndDate, 'MMM DD, YYYY'),
      isBottomRed: false,
    },
  };
};

const getNextCreditReportSection = (): BillingHeaderSectionProps | null => {
  const nextReportDate = nextCreditReportDate(new Date());
  return {
    column1: {
      top: 'Next credit report',
      bottom: (
        <div className="billing__header-next-report-date-container">
          {formatDateTimeCell(nextReportDate, 'MMM DD, YYYY')}
          <Info
            tooltipText="Growegy reports automatically to Equifax and Experian monthly. Paying the account in full prior to 11:59 pm PST on the 14th of each month will report a lower credit utilization and can increase credit scores more rapidly. Paying on or before the due date will result in an on time payment being reported."
            tooltipWidth={256}
            tooltipPlace={'bottom'}
            isSmall={true}
            dataTest="billing__next-credit-report-info"
          />
        </div>
      ),
      isBottomRed: false,
    },
  };
};

export const BillingHeader = ({
  customer,
  invoices,
  onCancelClick,
  onSeeAnnualPlan,
  showEinBanner,
}: {
  customer: Customer;
  invoices: Invoice[];
  onCancelClick: () => void;
  onSeeAnnualPlan: () => void;
  showEinBanner: boolean;
}) => {
  const analytics = useAnalytics();

  const issuedInvoices = invoices.filter((x) => x.type === INVOICE_TYPE_ISSUED) as IssuedInvoice[];
  const sections: (BillingHeaderSectionProps | null)[] = [
    getCurrentPlanSectionProps({
      currentPlan: customer.planName,
      customerSubscription: customer.activeSubscription,
      invoices: issuedInvoices,
      changePlanOptions: customer.changePlanOptions,
      onCancelClick: () => onCancelClick(),
      onSeeAnnualClick: () => {
        onSeeAnnualPlan();
        analyticsTrack(analytics, AnalyticsEvent.BILLING_PLAN_ANNUAL_PLAN_VIEWED, {
          source: 'header',
        });
      },
      onReactivateClick: () => setReactivationDialogKey(uuidv4()),
    }),
    getInvoicesSection(issuedInvoices),
    getNextPaymentSection(customer.planName, issuedInvoices, customer.activeSubscription),
    getNextCreditReportSection(),
  ];
  const [reactivationDialogKey, setReactivationDialogKey] = useState<string | null>(null);
  const billingPlan = getBillingPlan(customer.planName);

  const hasUnpaidInvoices = invoices.some(isInvoicePayable);
  return (
    <div className="billing__header-container">
      <BillingHeaderSections
        sections={sections.filter((section) => section !== null) as BillingHeaderSectionProps[]}
      />
      <CanceledBanner customer={customer} invoices={invoices} />
      {showEinBanner && customer.taxId && <TaxIdRequiredBanner taxId={customer.taxId} />}
      {reactivationDialogKey && billingPlan && (
        <ConfirmModal
          key={reactivationDialogKey}
          title="You are pre-approved to reactivate your account"
          subTitle={
            hasUnpaidInvoices
              ? 'Please pay your outstanding invoices to reactivate your account.'
              : undefined
          }
          footer={{ type: 'show' }}
          ariaHideApp={false}
        >
          {hasUnpaidInvoices ? (
            <InvoiceTable
              invoices={invoices.filter(isInvoicePayable)}
              header="Outstanding invoices"
              context="reactivation"
            />
          ) : (
            <Reactivation billingPlan={billingPlan} offer={null} />
          )}
        </ConfirmModal>
      )}
    </div>
  );
};
